import { all, put, select, take, takeLatest, race } from "redux-saga/effects";
import {
  InitializeEditorPayload,
  ReduxAction,
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "constants/ReduxActionConstants";

import { fetchPageList, setAppMode, updateAppStore } from "actions/pageActions";
import { fetchDatasources } from "actions/datasourceActions";
import { fetchApplication } from "actions/applicationActions";
import AnalyticsUtil from "utils/AnalyticsUtil";
import { getCurrentApplication } from "selectors/applicationSelectors";
import { APP_MODE } from "reducers/entityReducers/appReducer";
import { getAppStore } from "constants/AppConstants";
import { initEditorError } from "../actions/initActions";

function* initializeEditorSaga(
  initializeEditorAction: ReduxAction<InitializeEditorPayload>,
) {
  const { applicationId } = initializeEditorAction.payload;
  try {
    yield put(setAppMode(APP_MODE.EDIT));
    yield all([
      put(fetchPageList(applicationId, APP_MODE.EDIT)),
      put(fetchApplication(applicationId, APP_MODE.EDIT)),
    ]);

    // Workspace desktop bootstrap gate (Phase 4 decouple).
    // The desktop (MainContainer / DoclLayoutSettings iframes / Sidebar) renders
    // from ONLY the application (orgId/app/auth params + saved layout load) and
    // the page list (applicationId / currentPageId). It does NOT read the page
    // DSL, actions, plugins, datasources*, or widget data — those feed the dead
    // canvas/widget runtime removed in Phase 5. So we block init on ONLY these
    // two successes, and nothing here white-screens the desktop if it is slow.
    // (*Sidebar reads datasources but tolerates loading/empty + 5s polling.)
    const resultOfPrimaryCalls = yield race({
      success: all([
        take(ReduxActionTypes.FETCH_PAGE_LIST_SUCCESS),
        take(ReduxActionTypes.FETCH_APPLICATION_SUCCESS),
      ]),
      failure: take([
        ReduxActionErrorTypes.FETCH_PAGE_LIST_ERROR,
        ReduxActionErrorTypes.FETCH_APPLICATION_ERROR,
      ]),
    });

    if (resultOfPrimaryCalls.failure) {
      yield put(initEditorError());
      return;
    }

    // Services are org-scoped (GET v2/datasources?organizationId=...) and the
    // org id only reaches the store once fetchPageList resolves, via
    // SET_CURRENT_ORG_ID. Dispatching this in the all([]) above sent the
    // *previous* workspace's org id when switching workspaces, so the sidebar's
    // stale tree came back re-confirmed and only the 5s poll repaired it.
    // Firing it after the gate guarantees the org id belongs to this app.
    yield put(fetchDatasources());

    yield put(updateAppStore(getAppStore(applicationId)));

    const currentApplication = yield select(getCurrentApplication);

    const appName = currentApplication ? currentApplication.name : "";
    const appId = currentApplication ? currentApplication.id : "";

    AnalyticsUtil.logEvent("EDITOR_OPEN", {
      appId: appId,
      appName: appName,
    });

    yield put({
      type: ReduxActionTypes.INITIALIZE_EDITOR_SUCCESS,
    });
  } catch (e) {
    yield put(initEditorError());
    return;
  }
}

export default function* watchInitSagas() {
  yield all([
    takeLatest(ReduxActionTypes.INITIALIZE_EDITOR, initializeEditorSaga),
  ]);
}
